"use client";

import {
  EVENT_ATTACHMENT_MAX_BYTES,
  EVENT_ATTACHMENT_MAX_COUNT,
  EVENT_ATTACHMENT_MAX_MB,
} from "@bg-bgi/magnus-sdk";
import { Button } from "@workspace/ui/components/button";
import { Label } from "@workspace/ui/components/label";
import { Loader2, Paperclip, X } from "lucide-react";
import {
  type Ref,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ComposerUploadsSource } from "../attachments";
import { type ComposerAttachment, useAttachments } from "../use-attachments";

export type AttachFilesState = {
  files: ComposerAttachment[];
  isUploading: boolean;
};

export type AttachFilesHandle = {
  /** Delete the uploaded rows — the surface was cancelled, nothing sent. */
  discard: () => void;
  /** Forget the files WITHOUT deleting — they now belong to a sent action. */
  reset: () => void;
};

/**
 * "Attach a file" field for the approval card's editable `attachments`
 * arg. Uploads go through the host's `uploads` source (the same seam the
 * composer stages through), so the resulting upload ids are readable back
 * by server tools.
 *
 * Ownership: the caller decides on close whether uploads were consumed
 * (`reset`) or abandoned (`discard`, which deletes the rows).
 */
export function AttachFilesField({
  handleRef,
  hint,
  label = "Attachments",
  maxFiles = EVENT_ATTACHMENT_MAX_COUNT,
  onStateChange,
  uploads,
}: {
  handleRef: Ref<AttachFilesHandle>;
  /** Copy under the button, e.g. what the files will be used for. */
  hint: string;
  label?: string;
  /**
   * How many files this field may add, when that's fewer than the
   * invitation-wide cap — the approval card passes the slots left after the
   * attachments the model already put on the call. `0` disables adding.
   */
  maxFiles?: number;
  onStateChange: (state: AttachFilesState) => void;
  uploads: ComposerUploadsSource;
}) {
  const source = useMemo(() => ({ ...uploads, maxFiles }), [uploads, maxFiles]);
  const { files, isUploading, addFiles, removeFile, clearFiles } =
    useAttachments(source);
  const [oversizeNames, setOversizeNames] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const buttonId = useId();
  const filesRef = useRef(files);
  filesRef.current = files;

  useEffect(() => {
    onStateChange({
      files: files.filter((f) => f.status === "uploaded"),
      isUploading,
    });
  }, [files, isUploading, onStateChange]);

  useImperativeHandle(
    handleRef,
    () => ({
      discard: () => {
        for (const f of filesRef.current) {
          removeFile(f.id);
        }
        filesRef.current = [];
      },
      reset: () => {
        clearFiles();
        // State updates land next render; empty the mirror NOW so a discard
        // that follows in the same tick can't delete the just-sent uploads.
        filesRef.current = [];
      },
    }),
    [removeFile, clearFiles]
  );

  const handlePickedFiles = (list: FileList | null) => {
    if (!list) {
      return;
    }
    const accepted: File[] = [];
    const tooBig: string[] = [];
    for (const file of Array.from(list)) {
      if (file.size > EVENT_ATTACHMENT_MAX_BYTES) {
        tooBig.push(file.name);
      } else {
        accepted.push(file);
      }
    }
    setOversizeNames(tooBig);
    if (accepted.length > 0) {
      addFiles(accepted).catch(() => {
        // per-file failures already surface as chip status + toast
      });
    }
  };

  let footnote = hint;
  if (oversizeNames.length > 0) {
    footnote = `Skipped ${oversizeNames.map((n) => `"${n}"`).join(", ")} — attachments are limited to ${EVENT_ATTACHMENT_MAX_MB} MB each.`;
  } else if (maxFiles === 0) {
    footnote = `Up to ${EVENT_ATTACHMENT_MAX_COUNT} files — remove one to attach another.`;
  }

  return (
    <div className="space-y-1.5">
      <Label htmlFor={buttonId}>{label}</Label>
      <input
        aria-label="Attach files"
        className="hidden"
        multiple
        onChange={(e) => {
          handlePickedFiles(e.target.files);
          e.target.value = "";
        }}
        ref={inputRef}
        type="file"
      />
      {files.length > 0 && (
        <ul className="space-y-1">
          {files.map((f) => (
            <li
              className="flex items-center gap-2 rounded-md border bg-muted/30 px-2 py-1 text-xs"
              key={f.id}
            >
              {f.status === "uploading" || f.status === "pending" ? (
                <Loader2 className="h-3 w-3 shrink-0 animate-spin text-muted-foreground" />
              ) : (
                <Paperclip className="h-3 w-3 shrink-0 text-muted-foreground" />
              )}
              <span className="min-w-0 flex-1 truncate" title={f.file.name}>
                {f.file.name}
              </span>
              {f.status === "error" && (
                <span className="shrink-0 text-destructive">failed</span>
              )}
              <button
                aria-label={`Remove ${f.file.name}`}
                className="shrink-0 rounded p-0.5 text-muted-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => removeFile(f.id)}
                type="button"
              >
                <X className="h-3 w-3" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <Button
        disabled={files.length >= maxFiles}
        id={buttonId}
        onClick={() => inputRef.current?.click()}
        size="sm"
        type="button"
        variant="outline"
      >
        <Paperclip className="h-3.5 w-3.5" />
        Attach a file
      </Button>
      <p className="text-muted-foreground text-xs">{footnote}</p>
    </div>
  );
}
